/**
 * ConnectionRequiredContent — S4 / RFC §4.5 (consumer Drive scenario).
 *
 * Renders when the step status is BLOCKED with reason
 * CONNECTION_REQUIRED. Shows the consumer-facing "why are we asking"
 * pitch, lets the user pick a tier (within the integration's
 * `allowedTiers`), and opens the broker Connect popup (Composio
 * today; previously Nango — see ADR-0005). When the popup closes we
 * poll `connectionRequirement` once to detect that the step is no
 * longer blocked, then refetch the plan so the parent panel
 * transitions to ACTIVE_WORK.
 *
 * We do NOT call `registerConnection` from the popup-close path: the
 * broker webhook (S13) is the source of truth, and `registerConnection`
 * is itself idempotent on (tier, ownerKey, integrationId) — racing
 * the webhook is harmless but unnecessary. If the webhook is slow or
 * unavailable in dev, the user can hit "I just connected" to nudge a
 * refetch.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {
  ConnectionTier,
  PlanStep,
  StepStatusReason,
} from '../../../../types/generated/graphql';
import {colors} from '../../../../theme/colors';
import {GET_CONNECTION_REQUIREMENT, GET_PLAN} from '../../../../graphql/queries';
import {
  CREATE_CONNECT_SESSION,
  RECORD_ORG_USER_FALLBACK_CONSENT,
} from '../../../../graphql/mutations';
import {IntegrationLogo, getBrandColor} from '../../../../integrations';

interface Props {
  planId: string;
  step: PlanStep;
}

const TIER_LABELS: Record<ConnectionTier, string> = {
  [ConnectionTier.User]: 'My account',
  [ConnectionTier.Org]: 'Organization account',
  [ConnectionTier.Run]: 'Just for this run',
  [ConnectionTier.Platform]: 'Platform',
};

const TIER_BLURB: Record<ConnectionTier, string> = {
  [ConnectionTier.User]:
    'Reuse this connection across all your future plans on Planapus.',
  [ConnectionTier.Org]:
    'Share this connection with your whole org. Org admins can revoke it from Settings.',
  [ConnectionTier.Run]:
    'One-time access. We discard the credential as soon as this run finishes.',
  [ConnectionTier.Platform]: 'Managed by Planapus.',
};

export const ConnectionRequiredContent: React.FC<Props> = ({planId, step}) => {
  const {data, loading, error, refetch} = useQuery(GET_CONNECTION_REQUIREMENT, {
    variables: {runId: planId, stepId: step.id},
    fetchPolicy: 'cache-and-network',
  });

  const [createSession, {loading: sessionLoading}] = useMutation(
    CREATE_CONNECT_SESSION,
    {
      // After creating a session we don't need to refetch anything —
      // the popup-close path handles refetch.
      refetchQueries: [],
    },
  );

  // S9 — ORG→USER fallback consent. When the resolution walk lands
  // on the consumer's USER tier because the org's ORG-tier connection
  // is missing, the consumer must explicitly opt in once per run
  // (per ADR-0002 / RFC §3.3). The mutation REPLACES the approved set
  // — no grandfathering, no account-wide consent.
  const [recordFallbackConsent, {loading: fallbackLoading}] = useMutation(
    RECORD_ORG_USER_FALLBACK_CONSENT,
  );

  const apollo = useApolloClient();
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<ConnectionTier | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const requirement = data?.connectionRequirement;

  const closePopupWatcher = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const handlePopupClosed = useCallback(async () => {
    closePopupWatcher();
    setPopupOpen(false);
    // Refetch the requirement first (cheap), then refetch the plan
    // so the parent transitions out of BLOCKED_CONNECTION_REQUIRED
    // mode if the connection landed. The broker webhook (S13;
    // Composio today, previously Nango) is the source of truth; if
    // it hasn't fired yet the user can hit "I just connected" again
    // or rely on the plan poll.
    try {
      await refetch();
      await apollo.refetchQueries({include: [GET_PLAN]});
    } catch (e: unknown) {
      console.warn('[ConnectionRequired] post-popup refetch failed', e);
    }
  }, [apollo, closePopupWatcher, refetch]);

  const watchPopup = useCallback(
    (popup: Window) => {
      setPopupOpen(true);
      closePopupWatcher();
      pollRef.current = window.setInterval(() => {
        if (popup.closed) {
          handlePopupClosed();
        }
      }, 500);
    },
    [closePopupWatcher, handlePopupClosed],
  );

  useEffect(() => {
    return () => closePopupWatcher();
  }, [closePopupWatcher]);

  const handleConnect = useCallback(
    async (tier: ConnectionTier) => {
      if (!requirement) return;
      setErrMsg(null);
      setSelectedTier(tier);
      try {
        const result = await createSession({
          variables: {
            integrationId: requirement.integrationId,
            tier,
            // For a JIT step-side flow the runId is always set; the
            // backend uses it to scope RUN-tier sessions and as
            // audit context for USER/ORG sessions.
            runId: planId,
            orgId: null,
          },
        });
        const url = result.data?.createConnectSession?.connectUrl;
        if (!url) {
          setErrMsg('Could not start the connection flow. Please try again.');
          return;
        }
        const popup = window.open(
          url,
          'planapus-connect',
          'width=600,height=720,resizable=yes,scrollbars=yes',
        );
        if (!popup) {
          setErrMsg(
            'Your browser blocked the popup. Please allow popups for this site and try again.',
          );
          return;
        }
        watchPopup(popup);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        setErrMsg(`Failed to start Connect session: ${msg}`);
        setSelectedTier(null);
      }
    },
    [createSession, planId, requirement, watchPopup],
  );

  if (loading && !data) {
    return (
      <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">
          Could not load connection requirement: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!requirement) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="info">
          This step is no longer blocked. Refreshing&hellip;
        </Alert>
      </Box>
    );
  }

  // S11 — preview-mode pitch is distinct from the live-consumer
  // pitch: this is the *creator* connecting their own test account
  // to dry-run the guide, not a magic-link consumer authorizing a
  // run. Override the manifest's consumerPitch when the resolver
  // signals PREVIEW_NEEDS_USER so the copy doesn't read like a JIT
  // ask of an end-user.
  const isPreviewNeedsUser =
    step.statusReason === StepStatusReason.PreviewNeedsUser;
  const reasonCopy = isPreviewNeedsUser
    ? `Preview mode runs against your own ${requirement.integrationId} account so you can test the guide without touching the org wallet or sending real messages. Connect once and re-use it across previews.`
    : requirement.consumerPitch ||
      `This step needs to talk to ${requirement.integrationId} on your behalf. Connecting takes about 30 seconds and we only ask for the scopes this step actually uses.`;

  const reasonChip =
    step.statusReason === StepStatusReason.OrgReconnectRequired
      ? 'Your org admin needs to reconnect this integration.'
      : step.statusReason === StepStatusReason.ConsumerWithheldFallback
      ? "You haven't approved an org→user fallback for this run."
      : isPreviewNeedsUser
      ? 'Preview-only — no real org data or downstream side-effects.'
      : null;

  // S9 — render the explicit "use my personal connection for this
  // run" CTA only when the block IS the missing ORG→USER consent
  // AND the integration manifest actually allows USER (otherwise
  // the consent would be meaningless and the server would reject
  // it). The CTA piggybacks on `requirement.allowedTiers` because
  // the resolver only includes USER there if the manifest allows it.
  const showOrgUserFallbackConsent =
    step.statusReason === StepStatusReason.ConsumerWithheldFallback &&
    requirement.allowedTiers.includes(ConnectionTier.User);

  const handleApproveOrgUserFallback = async () => {
    setErrMsg(null);
    try {
      await recordFallbackConsent({
        variables: {
          runId: planId,
          // Replaces the approved set — for a single-step JIT consent
          // we approve only this integration. If a later step needs
          // the same approval the user is prompted again (per
          // ADR-0002, no implicit carry-forward).
          approvedIntegrationIds: [requirement.integrationId],
        },
      });
      // The plan refetch will let the resolver re-walk and pick up
      // the consumer's USER connection (or, if there isn't one yet,
      // re-block with NO_CONNECTION + tier=USER and we render the
      // standard Connect-USER picker).
      await refetch();
      await apollo.refetchQueries({include: [GET_PLAN]});
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setErrMsg(`Could not record consent: ${msg}`);
    }
  };

  const tiers = requirement.allowedTiers.length
    ? requirement.allowedTiers
    : [requirement.defaultTier];

  const brandColor = getBrandColor(requirement.integrationId);

  return (
    <Box sx={{p: 3}}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IntegrationLogo
            integrationId={requirement.integrationId}
            name={requirement.integrationId}
            size={40}
          />
          <Typography variant="h6" sx={{fontWeight: 600}}>
            {isPreviewNeedsUser
              ? `Connect your test ${requirement.integrationId} account`
              : `Connect ${requirement.integrationId} to continue`}
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{color: colors.textSecondary}}>
          {reasonCopy}
        </Typography>

        {reasonChip && (
          <Alert severity="warning" sx={{py: 0.5}}>
            {reasonChip}
          </Alert>
        )}

        {showOrgUserFallbackConsent && (
          <Alert
            severity="info"
            sx={{py: 0.5}}
            action={
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={fallbackLoading}
                onClick={handleApproveOrgUserFallback}
              >
                {fallbackLoading ? 'Approving…' : 'Use my account'}
              </Button>
            }
          >
            Your org doesn't have a {requirement.integrationId} connection
            available. You can let this run proxy through your personal
            connection — just for this run.
          </Alert>
        )}

        {errMsg && <Alert severity="error">{errMsg}</Alert>}

        <Box>
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 600,
              letterSpacing: 0.5,
              display: 'block',
              mb: 1,
            }}
          >
            Choose how you want to connect
          </Typography>
          <Stack spacing={1.5}>
            {tiers.map((tier: ConnectionTier) => (
              <Box
                key={tier}
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 1.5,
                  p: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor:
                    tier === requirement.defaultTier
                      ? colors.blue[50]
                      : 'transparent',
                }}
              >
                <Box sx={{flex: 1}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                      {TIER_LABELS[tier as ConnectionTier]}
                    </Typography>
                    {tier === requirement.defaultTier && (
                      <Chip
                        label="Recommended"
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: colors.blue[100],
                          color: colors.blue[700],
                          fontWeight: 500,
                          '& .MuiChip-label': {fontSize: '0.65rem'},
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{color: colors.textSecondary}}
                  >
                    {TIER_BLURB[tier as ConnectionTier]}
                  </Typography>
                </Box>
                <Button
                  variant={
                    tier === requirement.defaultTier ? 'contained' : 'outlined'
                  }
                  size="small"
                  startIcon={
                    sessionLoading && selectedTier === tier ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <OpenInNewIcon />
                    )
                  }
                  disabled={sessionLoading || popupOpen}
                  onClick={() => handleConnect(tier as ConnectionTier)}
                  sx={
                    tier === requirement.defaultTier && brandColor
                      ? {
                          bgcolor: brandColor,
                          '&:hover': {
                            bgcolor: brandColor,
                            filter: 'brightness(0.92)',
                          },
                        }
                      : undefined
                  }
                >
                  Connect
                </Button>
              </Box>
            ))}
          </Stack>
        </Box>

        {popupOpen && (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={handlePopupClosed}
              >
                I just connected
              </Button>
            }
          >
            Complete the connection in the popup window. We'll detect when
            you're done.
          </Alert>
        )}

        <Typography variant="caption" sx={{color: colors.textSecondary}}>
          Why are we asking? Planapus only requests the integration scopes
          this specific step needs. You can review and revoke any
          connection from your account settings at any time.
        </Typography>
      </Stack>
    </Box>
  );
};

export default ConnectionRequiredContent;
